import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import type {
  Opportunity, OpportunityCreate, OpportunityUpdate, OpportunityStage,
  OpportunityActivity,
  PaginatedResponse,
} from '@/types'

interface UseOpportunitiesParams {
  page?: number
  perPage?: number
  search?: string
  stage?: OpportunityStage | null
  ownerId?: string | null
  leadId?: string | null
}

export function useOpportunities(params: UseOpportunitiesParams = {}) {
  return useQuery({
    queryKey: ['opportunities', params],
    queryFn: async () => {
      const query: Record<string, string | number> = {
        page: params.page || 1,
        per_page: params.perPage || 100,
      }
      if (params.search) query.search = params.search
      if (params.stage) query.stage = params.stage
      if (params.ownerId) query.owner_id = params.ownerId
      if (params.leadId) query.lead_id = params.leadId
      const { data } = await api.get<PaginatedResponse<Opportunity>>('/v1/opportunities/', { params: query })
      return data
    },
  })
}

export function useOpportunity(oppId: string | null) {
  return useQuery({
    queryKey: ['opportunities', oppId],
    queryFn: async () => {
      const { data } = await api.get<Opportunity>(`/v1/opportunities/${oppId}`)
      return data
    },
    enabled: !!oppId,
  })
}

export function useCreateOpportunity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (oppData: OpportunityCreate) => {
      const { data } = await api.post<Opportunity>('/v1/opportunities/', oppData)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['opportunities'] })
      qc.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

export function useUpdateOpportunity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ oppId, data: oppData }: { oppId: string; data: OpportunityUpdate }) => {
      const { data } = await api.put<Opportunity>(`/v1/opportunities/${oppId}`, oppData)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['opportunities'] }),
  })
}

export function useMoveOpportunityStage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ oppId, stage, lossReason, lossDetails }: { oppId: string; stage: OpportunityStage; lossReason?: string; lossDetails?: string }) => {
      const { data } = await api.patch<Opportunity>(`/v1/opportunities/${oppId}/stage`, {
        stage,
        loss_reason: lossReason || null,
        loss_details: lossDetails || null,
      })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['opportunities'] })
      qc.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

export function useDeleteOpportunity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (oppId: string) => {
      await api.delete(`/v1/opportunities/${oppId}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['opportunities'] })
      qc.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

// ==========================================
// Activity Timeline Hooks
// ==========================================

export function useOpportunityActivities(oppId: string | null) {
  return useQuery({
    queryKey: ['opportunities', oppId, 'activities'],
    queryFn: async () => {
      const { data } = await api.get<OpportunityActivity[]>(`/v1/opportunities/${oppId}/activities`)
      return data
    },
    enabled: !!oppId,
  })
}

export function useCreateActivity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ oppId, channel, content }: { oppId: string; channel: string; content: string }) => {
      const { data } = await api.post<OpportunityActivity>(`/v1/opportunities/${oppId}/activities`, { channel, content })
      return data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['opportunities', data.opportunity_id, 'activities'] })
    },
  })
}

export function useUpdateActivity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ oppId, activityId, channel, content }: { oppId: string; activityId: string; channel?: string; content?: string }) => {
      const body: Record<string, unknown> = {}
      if (channel !== undefined) body.channel = channel
      if (content !== undefined) body.content = content
      const { data } = await api.put<OpportunityActivity>(`/v1/opportunities/${oppId}/activities/${activityId}`, body)
      return data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['opportunities', data.opportunity_id, 'activities'] })
    },
  })
}

export function useDeleteActivity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ oppId, activityId }: { oppId: string; activityId: string }) => {
      await api.delete(`/v1/opportunities/${oppId}/activities/${activityId}`)
      return { oppId, activityId }
    },
    onSuccess: ({ oppId }) => {
      qc.invalidateQueries({ queryKey: ['opportunities', oppId, 'activities'] })
    },
  })
}



// ============================================================================
// Anexos (Documentos) da Oportunidade
// ============================================================================

export interface OpportunityAttachment {
  id: string
  opportunity_id: string
  file_name: string
  file_path: string
  file_type: string | null
  file_size: number | null
  uploaded_by: string | null
  uploader_name: string | null
  created_at: string
}

export function useOpportunityAttachments(oppId: string | null) {
  return useQuery({
    queryKey: ['opportunities', oppId, 'attachments'],
    queryFn: async () => {
      const { data } = await api.get<OpportunityAttachment[]>(`/v1/opportunities/${oppId}/attachments`)
      return data
    },
    enabled: !!oppId,
  })
}

export function useUploadOpportunityAttachment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ oppId, file }: { oppId: string; file: File }) => {
      const fd = new FormData()
      fd.append('file', file)
      const { data } = await api.post<OpportunityAttachment>(
        `/v1/opportunities/${oppId}/attachments`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 5 * 60_000 },
      )
      return data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['opportunities', data.opportunity_id, 'attachments'] })
    },
  })
}

export function useDeleteOpportunityAttachment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ oppId, attId }: { oppId: string; attId: string }) => {
      await api.delete(`/v1/opportunities/${oppId}/attachments/${attId}`)
      return { oppId, attId }
    },
    onSuccess: ({ oppId }) => {
      qc.invalidateQueries({ queryKey: ['opportunities', oppId, 'attachments'] })
    },
  })
}

export interface ForecastOpportunityItem {
  id: string
  name: string
  value: number
  probability: number
  stage: string
  expected_close_date: string | null
  lead_title: string | null
  company_name: string | null
}

export interface ForecastOwnerSummary {
  owner_id: string
  owner_name: string
  owner_avatar_url: string | null
  gross_total: number
  weighted_total: number
  count: number
  opportunities: ForecastOpportunityItem[]
}

export interface ForecastResponse {
  start_date: string
  end_date: string
  owners: ForecastOwnerSummary[]
  totals: { gross: number; weighted: number; count: number }
}

export function useForecast(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['forecast', startDate, endDate],
    queryFn: async () => {
      const { data } = await api.get<ForecastResponse>('/v1/opportunities/forecast/summary', {
        params: { start_date: startDate, end_date: endDate },
      })
      return data
    },
    enabled: !!startDate && !!endDate,
  })
}

export interface NextBestActionResponse {
  action: string
  reasoning: string
  deadline_days: number
  channel: string
  urgency: string
}

export function useNextBestAction() {
  return useMutation({
    mutationFn: async (oppId: string) => {
      const { data } = await api.post<NextBestActionResponse>(`/v1/opportunities/${oppId}/next-best-action`)
      return data
    },
  })
}


export interface FunnelStage {
  stage: string
  label: string
  count: number
  value: number
  conversion_from_prev_pct: number | null
}

export interface VelocityByOwner {
  owner_id: string
  owner_name: string
  avg_cycle_days: number | null
  won_count: number
}

export interface FunnelVelocityResponse {
  period_days: number
  funnel: FunnelStage[]
  avg_cycle_days_won: number | null
  win_rate_pct: number | null
  won_count: number
  lost_count: number
  velocity_by_owner: VelocityByOwner[]
}

export function useFunnelVelocity(periodDays: number) {
  return useQuery({
    queryKey: ['funnel-velocity', periodDays],
    queryFn: async () => {
      const { data } = await api.get<FunnelVelocityResponse>('/v1/opportunities/insights/funnel-velocity', {
        params: { period_days: periodDays },
      })
      return data
    },
  })
}

export interface AgingItem {
  id: string
  name: string
  stage: string
  stage_label: string
  value: number
  probability: number
  owner_id: string | null
  owner_name: string | null
  company_name: string | null
  expected_close_date: string | null
  days_to_close: number | null
  days_since_last_activity: number | null
  days_in_current_stage: number
  days_since_created: number
  next_task_title: string | null
  next_task_type: string | null
  next_task_due_date: string | null
  days_until_next_task: number | null
  has_overdue_task: boolean
  overdue_task_days: number | null
  risk: 'high' | 'medium' | 'low'
  risk_reason: string
}

export interface AgingResponse {
  total: number
  high_risk_count: number
  medium_risk_count: number
  low_risk_count: number
  items: AgingItem[]
}

export function useAgingReport(daysNoActivityThreshold = 14) {
  return useQuery({
    queryKey: ['aging-report', daysNoActivityThreshold],
    queryFn: async () => {
      const { data } = await api.get<AgingResponse>('/v1/opportunities/insights/aging', {
        params: { days_no_activity_threshold: daysNoActivityThreshold },
      })
      return data
    },
  })
}

export interface LossReasonItem {
  reason: string
  count: number
  total_value: number
  avg_deal_size: number
  pct_of_count: number
  pct_of_value: number
}

export interface LossReasonsResponse {
  period_days: number
  total_lost_count: number
  total_lost_value: number
  reasons: LossReasonItem[]
}

export function useLossReasons(periodDays = 180) {
  return useQuery({
    queryKey: ['loss-reasons', periodDays],
    queryFn: async () => {
      const { data } = await api.get<LossReasonsResponse>('/v1/opportunities/insights/loss-reasons', {
        params: { period_days: periodDays },
      })
      return data
    },
  })
}
